import React, { useEffect } from 'react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Center,
  Flex,
  Heading,
  IconButton,
  Image,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { createCustomWifiIcon } from 'clipsal-cortex-icons/src/custom-dynamic-icons';
import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';

import { useReduxDispatch } from '../../../app/store';
import chargerOfflineImg from '../../../assets/images/wifi_offline.svg';
import chargerOnlineImg from '../../../assets/images/wifi_online.svg';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { fetchEvChargerDetails, selectEvChargerData } from '../dashboard/evChargerSlice';
import { setIsEditing } from './wifiConfigSlice';

/* istanbul ignore next -- @preserve */
export const DeviceWifiConfiguration = () => {
  const { siteDeviceId, hasFetchedEvChargerDetails, status } = useSelector(selectEvChargerData);
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const bgColor = useColorModeValue('customGrey.700', 'customGrey.100');
  const textColor = useColorModeValue('customGrey.100', 'customGrey.700');
  const isOnline = !['Unavailable_Disconnect', 'Unavailable_Restart', 'Unavailable_Charger', 'Faulted'].includes(
    status
  );
  const wifiImg = isOnline ? chargerOnlineImg : chargerOfflineImg;
  const WifiIcon = createCustomWifiIcon(undefined, '');

  useEffect(() => {
    if (siteDeviceId && !hasFetchedEvChargerDetails) {
      dispatch(fetchEvChargerDetails(siteDeviceId));
    }
  }, [siteDeviceId, hasFetchedEvChargerDetails, dispatch]);

  const handleWifiEdit = () => {
    dispatch(setIsEditing(true));
    navigate('../device-power-up-instructions?backURL=../device-wifi-configuration');
  };

  const boxProps = {
    bg: '#E7E6E6',
    _dark: {
      bg: 'customGrey.800',
    },
    py: 6,
    px: 4,
    display: 'flex',
    position: 'relative' as const,
  };

  return (
    <SlidingAnimationPageBase
      title={t('Hardware.hardware')}
      backURL="../device-details"
      customNavigationDirection="forward"
    >
      {!hasFetchedEvChargerDetails ? (
        <CenteredLoader />
      ) : (
        <Flex direction={'column'} w="100%">
          <Center flexDirection="column" px={3} pb={6} textAlign="center">
            <Box position={'relative'}>
              <Image src={wifiImg} alt="Wi-Fi Connection" mb={4} />
            </Box>

            <Heading mt={2}>{t('EVSE.wifi connection')}</Heading>
            <Text mt={1}>{t('EVSE.wifi connection text')}</Text>
          </Center>

          <Flex direction={'column'} px={4}>
            <Text mb={1}>{t('Hardware.connection')}</Text>
            <Box {...boxProps} justifyContent="space-between" borderTopRightRadius={8} borderTopLeftRadius={8}>
              <Text data-testid="connection-status">
                {isOnline ? t('EVSE.wifi connection online') : t('EVSE.wifi connection offline')}
              </Text>
              <WifiIcon boxSize={5} mr={3} />
              <Box
                left={4}
                bottom={0}
                right={0}
                position={'absolute'}
                w={'calc(100% - 1rem)'}
                h="1px"
                bg={'rgba(151, 151, 151, 0.3)'}
              />
            </Box>
            <Box
              {...boxProps}
              borderBottomLeftRadius={8}
              borderBottomRightRadius={39}
              mt={0}
              data-testid="edit-wifi-button"
              onClick={handleWifiEdit}
            >
              {t('EVSE.edit wifi network')}
              <Popover>
                <PopoverTrigger>
                  <IconButton
                    height={'1em'}
                    min-width={'auto'}
                    size={'lg'}
                    aria-label={'info'}
                    variant={'ghost'}
                    icon={<InfoOutlineIcon />}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                  />
                </PopoverTrigger>
                <PopoverContent borderRadius={8} w="210px" border="none" bg={bgColor} textColor={textColor}>
                  <PopoverBody
                    borderRadius="14px"
                    boxShadow="rgba(0, 0, 0, 0.3) 0px 0px 10px 0px"
                    paddingTop="1em"
                    fontSize={'12px'}
                  >
                    {t('EVSE.edit wifi network info')}
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </Box>
          </Flex>
        </Flex>
      )}
    </SlidingAnimationPageBase>
  );
};
